import React, { useState, useEffect, useMemo } from 'react';
import {
    Briefcase,
    FileText,
    Presentation,
    Plus,
    Upload,
    Search,
    Trash2,
    Edit3,
    Eye,
    Shield,
    Users,
    Check,
    X,
    Loader2,
    Calendar,
    HardDrive,
    UserCheck,
    UserX,
    Filter,
    LayoutGrid,
    List,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    Lock,
    Unlock,
    ArrowUpDown
} from 'lucide-react';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import { SecureDocumentViewer } from '../../../components/document/SecureDocumentViewer';

interface JobSupportItem {
    id: number;
    title: string;
    description: string | null;
    category: string;
    file_path: string;
    original_file_name: string;
    file_type: string;
    file_size: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    storage_provider?: string;
    creator?: { id: number; name: string; email: string };
    access_assignments_count?: number;
    assigned_students?: Array<{ id: number; name: string; email: string }>;
}

interface StudentAccessItem {
    id: number;
    name: string;
    email: string;
    phone: string | null;
    course_name: string | null;
    cohorts: string[];
    has_job_support: boolean;
    has_general_access: boolean;
    assigned_materials_count: number;
    assigned_material_ids: number[];
    created_at: string;
}

export default function JobSupportManager() {
    const { user, userRole } = useAuth();
    const isAdmin = userRole === 'admin';

    // Data states
    const [materials, setMaterials] = useState<JobSupportItem[]>([]);
    const [students, setStudents] = useState<StudentAccessItem[]>([]);
    const [cohorts, setCohorts] = useState<Array<{ id: number | string; name: string }>>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState<'materials' | 'access'>('materials');

    // Filter & View states for Materials
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedFormat, setSelectedFormat] = useState('All');
    const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
    const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'title' | 'size'>('newest');

    // Filter & Pagination states for Students
    const [studentSearch, setStudentSearch] = useState('');
    const [selectedCohort, setSelectedCohort] = useState('All');
    const [selectedAccessStatus, setSelectedAccessStatus] = useState<'all' | 'granted' | 'locked'>('all');
    const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
    const [studentCurrentPage, setStudentCurrentPage] = useState(1);
    const [studentsPerPage, setStudentsPerPage] = useState(15);

    // Modals
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [editModalDoc, setEditModalDoc] = useState<JobSupportItem | null>(null);
    const [assignModalDoc, setAssignModalDoc] = useState<JobSupportItem | null>(null);
    const [previewDoc, setPreviewDoc] = useState<JobSupportItem | null>(null);

    // Upload / Edit Form
    const [formTitle, setFormTitle] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [formCategory, setFormCategory] = useState('Project SOP');
    const [customCategory, setCustomCategory] = useState('');
    const [formFile, setFormFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Specific Material Assign Modal State
    const [assignTargetStudentIds, setAssignTargetStudentIds] = useState<number[]>([]);
    const [assignSaving, setAssignSaving] = useState(false);
    const [assignSearchQuery, setAssignSearchQuery] = useState('');

    const API_URL = (import.meta as any).env.VITE_API_BASE_URL || 'http://localhost:8000/api';
    const token = localStorage.getItem('token');

    const categoryPresets = [
        'Project SOP',
        'Templates & Forms',
        'Interview Prep',
        'Primavera P6 Guides',
        'Earned Value Analysis',
        'Resume & CV Tools',
        'Client Case Studies',
        'General',
        '+ Custom Category',
    ];

    const loadData = async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        try {
            const [matRes, studRes] = await Promise.all([
                fetch(`${API_URL}/instructor/job-support`, {
                    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
                }),
                fetch(`${API_URL}/instructor/job-support/students`, {
                    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
                }),
            ]);

            if (matRes.ok) {
                const matData = await matRes.json();
                setMaterials(matData.materials || []);
            }

            if (studRes.ok) {
                const studData = await studRes.json();
                setStudents(studData.students || []);
                setCohorts(studData.cohorts || []);
            }
        } catch (err) {
            console.error('Failed to load Job Support data:', err);
            toast.error('Failed to load Job Support data.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Reset student page on filter change
    useEffect(() => {
        setStudentCurrentPage(1);
    }, [studentSearch, selectedCohort, selectedAccessStatus, studentsPerPage]);

    const resetForm = () => {
        setFormTitle('');
        setFormDescription('');
        setFormCategory('Project SOP');
        setCustomCategory('');
        setFormFile(null);
        setEditModalDoc(null);
    };

    const handleOpenUpload = () => {
        resetForm();
        setUploadModalOpen(true);
    };

    const handleOpenEdit = (item: JobSupportItem) => {
        setEditModalDoc(item);
        setFormTitle(item.title);
        setFormDescription(item.description || '');
        if (categoryPresets.includes(item.category)) {
            setFormCategory(item.category);
            setCustomCategory('');
        } else {
            setFormCategory('+ Custom Category');
            setCustomCategory(item.category);
        }
        setFormFile(null);
        setUploadModalOpen(true);
    };

    const handleSubmitUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formTitle.trim()) {
            toast.error('Please enter a document title.');
            return;
        }

        if (!editModalDoc && !formFile) {
            toast.error('Please select a document file (.pdf, .docx, or .pptx).');
            return;
        }

        const resolvedCategory = formCategory === '+ Custom Category' ? (customCategory.trim() || 'General') : formCategory;

        setIsSubmitting(true);
        const formData = new FormData();
        formData.append('title', formTitle.trim());
        formData.append('description', formDescription.trim());
        formData.append('category', resolvedCategory);
        if (formFile) {
            formData.append('file', formFile);
        }

        try {
            const endpoint = editModalDoc
                ? `${API_URL}/instructor/job-support/${editModalDoc.id}`
                : `${API_URL}/instructor/job-support`;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                },
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                toast.success(editModalDoc ? 'Document updated successfully!' : 'Document uploaded successfully!');
                setUploadModalOpen(false);
                resetForm();
                loadData();
            } else {
                toast.error(data.message || 'Failed to save document.');
            }
        } catch (err: any) {
            toast.error(err.message || 'Error communicating with server.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (item: JobSupportItem) => {
        const result = await Swal.fire({
            title: 'Delete Document?',
            text: `Are you sure you want to permanently delete "${item.title}"? Students will immediately lose access to this document.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#475569',
        });

        if (!result.isConfirmed) return;

        try {
            const response = await fetch(`${API_URL}/instructor/job-support/${item.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
            });

            if (response.ok) {
                toast.success('Document deleted successfully.');
                loadData();
            } else {
                toast.error('Failed to delete document.');
            }
        } catch (err) {
            toast.error('Error deleting document.');
        }
    };

    const handleToggleStudentAccess = async (studentId: number, enable: boolean) => {
        try {
            const response = await fetch(`${API_URL}/instructor/job-support/students/toggle-access`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({
                    student_ids: [studentId],
                    enable: enable,
                }),
            });

            if (response.ok) {
                setStudents(prev =>
                    prev.map(s => (s.id === studentId ? { ...s, has_job_support: enable, has_general_access: enable } : s))
                );
                toast.success(enable ? 'Job Support access granted.' : 'Job Support access revoked.');
            } else {
                toast.error('Failed to update access.');
            }
        } catch (err) {
            toast.error('Network error updating student access.');
        }
    };

    const handleBulkAccessToggle = async (enable: boolean) => {
        if (selectedStudentIds.length === 0) {
            toast.error('Please select at least one student.');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/instructor/job-support/students/toggle-access`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({
                    student_ids: selectedStudentIds,
                    enable: enable,
                }),
            });

            if (response.ok) {
                setStudents(prev =>
                    prev.map(s =>
                        selectedStudentIds.includes(s.id)
                            ? { ...s, has_job_support: enable, has_general_access: enable }
                            : s
                    )
                );
                toast.success(
                    enable
                        ? `Job Support access granted to ${selectedStudentIds.length} student(s).`
                        : `Job Support access revoked for ${selectedStudentIds.length} student(s).`
                );
                setSelectedStudentIds([]);
            } else {
                toast.error('Failed to update student access.');
            }
        } catch (err) {
            toast.error('Network error during bulk access update.');
        }
    };

    const handleOpenAssignModal = (item: JobSupportItem) => {
        setAssignModalDoc(item);
        const assignedIds = students
            .filter(s => s.assigned_material_ids && s.assigned_material_ids.includes(item.id))
            .map(s => s.id);
        setAssignTargetStudentIds(assignedIds);
        setAssignSearchQuery('');
    };

    const handleSaveMaterialAssignments = async () => {
        if (!assignModalDoc) return;

        setAssignSaving(true);
        try {
            const response = await fetch(`${API_URL}/instructor/job-support/${assignModalDoc.id}/assign`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({
                    student_ids: assignTargetStudentIds,
                }),
            });

            if (response.ok) {
                toast.success('Material student assignments saved successfully!');
                setAssignModalDoc(null);
                loadData();
            } else {
                toast.error('Failed to update assignments.');
            }
        } catch (err) {
            toast.error('Error saving assignments.');
        } finally {
            setAssignSaving(false);
        }
    };

    const formatBytes = (bytes: number | null) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return 'N/A';
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getFormatBadge = (type: string) => {
        const t = (type || '').toLowerCase().replace('.', '');
        if (t === 'pdf') {
            return {
                label: 'PDF',
                colorClass: 'text-rose-500 bg-rose-500/10 border-rose-500/20',
                accentColor: '#ef4444',
                icon: FileText,
            };
        }
        if (t === 'docx' || t === 'doc') {
            return {
                label: 'DOCX',
                colorClass: 'text-sky-500 bg-sky-500/10 border-sky-500/20',
                accentColor: '#0284c7',
                icon: FileText,
            };
        }
        if (t === 'pptx' || t === 'ppt') {
            return {
                label: 'PPTX',
                colorClass: 'text-amber-500 bg-amber-500/10 border-amber-500/20',
                accentColor: '#f59e0b',
                icon: Presentation,
            };
        }
        return {
            label: t.toUpperCase(),
            colorClass: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20',
            accentColor: '#10b981',
            icon: FileText,
        };
    };

    // Derived Statistics
    const totalStorageBytes = useMemo(() => {
        return materials.reduce((acc, m) => acc + (m.file_size || 0), 0);
    }, [materials]);

    const totalStudentsWithAccess = useMemo(() => {
        return students.filter(s => s.has_job_support).length;
    }, [students]);

    const formatDistribution = useMemo(() => {
        const counts: { pdf: number; docx: number; pptx: number } = { pdf: 0, docx: 0, pptx: 0 };
        materials.forEach(m => {
            const t = (m.file_type || '').toLowerCase();
            if (t.includes('pdf')) counts.pdf++;
            else if (t.includes('doc')) counts.docx++;
            else if (t.includes('ppt')) counts.pptx++;
        });
        return counts;
    }, [materials]);

    // Filtered Materials
    const uniqueCategories = useMemo(() => {
        return ['All', ...Array.from(new Set(materials.map(m => m.category).filter(Boolean)))];
    }, [materials]);

    const filteredMaterials = useMemo(() => {
        let list = materials.filter(m => {
            const matchesCategory = selectedCategory === 'All' || m.category === selectedCategory;
            const matchesFormat =
                selectedFormat === 'All' ||
                (selectedFormat === 'pdf' && m.file_type.includes('pdf')) ||
                (selectedFormat === 'docx' && (m.file_type.includes('doc') || m.file_type.includes('docx'))) ||
                (selectedFormat === 'pptx' && (m.file_type.includes('ppt') || m.file_type.includes('pptx')));

            const matchesSearch =
                m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (m.description && m.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (m.category && m.category.toLowerCase().includes(searchQuery.toLowerCase()));

            return matchesCategory && matchesFormat && matchesSearch;
        });

        // Sorting
        list.sort((a, b) => {
            if (sortBy === 'newest') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            if (sortBy === 'oldest') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            if (sortBy === 'title') return a.title.localeCompare(b.title);
            if (sortBy === 'size') return (b.file_size || 0) - (a.file_size || 0);
            return 0;
        });

        return list;
    }, [materials, selectedCategory, selectedFormat, searchQuery, sortBy]);

    // Filtered Students & Pagination
    const filteredStudents = useMemo(() => {
        return students.filter(s => {
            const matchesCohort =
                selectedCohort === 'All' ||
                s.cohorts.some(c => c.toLowerCase().includes(selectedCohort.toLowerCase()));

            const matchesStatus =
                selectedAccessStatus === 'all' ||
                (selectedAccessStatus === 'granted' && s.has_job_support) ||
                (selectedAccessStatus === 'locked' && !s.has_job_support);

            const matchesSearch =
                s.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
                s.email.toLowerCase().includes(studentSearch.toLowerCase()) ||
                (s.phone && s.phone.toLowerCase().includes(studentSearch.toLowerCase())) ||
                (s.course_name && s.course_name.toLowerCase().includes(studentSearch.toLowerCase()));

            return matchesCohort && matchesStatus && matchesSearch;
        });
    }, [students, selectedCohort, selectedAccessStatus, studentSearch]);

    const totalStudentPages = Math.ceil(filteredStudents.length / studentsPerPage) || 1;
    const paginatedStudents = useMemo(() => {
        const start = (studentCurrentPage - 1) * studentsPerPage;
        return filteredStudents.slice(start, start + studentsPerPage);
    }, [filteredStudents, studentCurrentPage, studentsPerPage]);

    const handleSelectAllFilteredStudents = () => {
        const allFilteredIds = filteredStudents.map(s => s.id);
        if (selectedStudentIds.length === allFilteredIds.length && allFilteredIds.length > 0) {
            setSelectedStudentIds([]);
        } else {
            setSelectedStudentIds(allFilteredIds);
        }
    };

    return (
        <div className="users-container animate-fade-in-up">
            <style>{`
                .staff-scope .users-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 2rem;
                    gap: 1.5rem;
                }

                @media (max-width: 768px) {
                    .staff-scope .users-header {
                        flex-direction: column;
                        align-items: stretch;
                        margin-bottom: 1.5rem;
                    }
                    .staff-scope .header-actions {
                        width: 100%;
                        display: flex;
                        gap: 0.75rem;
                    }
                    .staff-scope .btn-add-user,
                    .staff-scope .btn-export {
                        flex: 1;
                        justify-content: center;
                        height: 48px;
                    }
                }

                .staff-scope .users-header h2 {
                    font-size: 1.75rem;
                    font-weight: 850;
                    color: var(--index-text-heading);
                    margin: 0.4rem 0 0.25rem 0;
                    letter-spacing: -0.02em;
                }

                .staff-scope .users-header p {
                    color: var(--index-text-secondary);
                    font-size: 0.95rem;
                    font-weight: 500;
                    margin: 0;
                    max-width: 700px;
                    line-height: 1.5;
                }

                .staff-scope .header-actions {
                    display: flex;
                    gap: 0.75rem;
                    align-items: center;
                }

                .staff-scope .btn-export {
                    background-color: var(--index-hover-bg);
                    color: var(--index-text-heading);
                    border: 1.5px solid var(--index-border-color);
                    padding: 0.65rem 1.25rem;
                    border-radius: 12px;
                    font-weight: 750;
                    font-size: 0.88rem;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    transition: all 0.2s ease;
                    height: 48px;
                }

                .staff-scope .btn-export:hover {
                    background-color: var(--index-border-color);
                    color: var(--index-primary-color);
                }

                .staff-scope .btn-add-user {
                    background-color: var(--index-primary-color);
                    color: white;
                    padding: 0.65rem 1.4rem;
                    border-radius: 12px;
                    border: none;
                    font-weight: 800;
                    font-size: 0.88rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                    box-shadow: 0 4px 14px color-mix(in srgb, var(--index-primary-color) 25%, transparent);
                    transition: all 0.2s ease;
                    height: 48px;
                    white-space: nowrap;
                }

                .staff-scope .btn-add-user:hover {
                    opacity: 0.92;
                    transform: translateY(-1px);
                }

                /* Stats Grid */
                .staff-scope .stats-grid-dashboard {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
                    gap: 1.25rem;
                    margin-bottom: 2rem;
                }

                .staff-scope .stat-card-premium {
                    background: var(--index-card-bg, #ffffff);
                    border: 1.5px solid var(--index-border-color, #e2e8f0);
                    border-radius: 20px;
                    padding: 1.25rem 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 1.25rem;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02);
                    transition: all 0.25s ease;
                }

                .staff-scope .stat-card-premium:hover {
                    transform: translateY(-2px);
                    border-color: var(--index-primary-color);
                    box-shadow: 0 10px 20px -5px rgba(0,0,0,0.06);
                }

                .staff-scope .stat-icon-wrapper {
                    width: 52px;
                    height: 52px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                /* Tab Segment Bar */
                .staff-scope .job-support-tab-bar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1rem;
                    border-bottom: 1.5px solid var(--index-border-color, #e2e8f0);
                    padding-bottom: 0.75rem;
                    margin-bottom: 1.75rem;
                    flex-wrap: wrap;
                }

                .staff-scope .job-support-tab-btn {
                    padding: 0.65rem 1.35rem;
                    border-radius: 12px;
                    font-weight: 800;
                    font-size: 0.82rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    transition: all 0.2s;
                    border: 1.5px solid transparent;
                }

                .staff-scope .job-support-tab-btn.active {
                    background: var(--index-primary-color);
                    color: #ffffff;
                    box-shadow: 0 4px 12px color-mix(in srgb, var(--index-primary-color) 25%, transparent);
                }

                .staff-scope .job-support-tab-btn:not(.active) {
                    background: var(--index-card-bg, #ffffff);
                    color: var(--index-text-secondary, #64748b);
                    border-color: var(--index-border-color, #e2e8f0);
                }

                .staff-scope .job-support-tab-btn:not(.active):hover {
                    color: var(--index-text-heading);
                    border-color: var(--index-border-subtle);
                }

                /* Standard Search & Filter Belt */
                .staff-scope .search-filter-belt {
                    display: flex;
                    gap: 1rem;
                    margin-bottom: 1.75rem;
                    flex-wrap: wrap;
                    width: 100%;
                }

                .staff-scope .search-box-wrapper {
                    flex: 1;
                    min-width: 260px;
                    height: 48px;
                    background: var(--index-card-bg, #ffffff);
                    border: 1.5px solid var(--index-border-color, #e2e8f0);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    padding: 0 1rem;
                    gap: 10px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                    transition: border-color 0.2s, box-shadow 0.2s;
                }

                .staff-scope .search-box-wrapper:focus-within {
                    border-color: var(--index-primary-color);
                    box-shadow: 0 0 0 3px color-mix(in srgb, var(--index-primary-color) 20%, transparent);
                }

                .staff-scope .search-input {
                    border: none !important;
                    background: transparent !important;
                    outline: none !important;
                    width: 100% !important;
                    font-weight: 600 !important;
                    font-size: 0.9rem !important;
                    color: var(--index-text-heading) !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    height: auto !important;
                    box-shadow: none !important;
                }

                .staff-scope .search-input::placeholder {
                    color: var(--index-text-faint, #94a3b8) !important;
                }

                .staff-scope .filter-dropdown-wrapper {
                    height: 48px;
                    background: var(--index-card-bg, #ffffff);
                    border: 1.5px solid var(--index-border-color, #e2e8f0);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    padding: 0 1rem;
                    gap: 8px;
                    font-weight: 600;
                    color: var(--index-text-secondary);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                    transition: border-color 0.2s, box-shadow 0.2s;
                }

                .staff-scope .filter-dropdown-wrapper:hover {
                    border-color: var(--index-border-subtle);
                }

                .staff-scope .filter-dropdown-wrapper:focus-within {
                    border-color: var(--index-primary-color);
                    box-shadow: 0 0 0 3px color-mix(in srgb, var(--index-primary-color) 20%, transparent);
                }

                .staff-scope .filter-label {
                    font-size: 0.82rem;
                    font-weight: 750;
                    color: var(--index-text-faint);
                    white-space: nowrap;
                }

                .staff-scope .filter-select {
                    border: none !important;
                    background: transparent !important;
                    outline: none !important;
                    font-weight: 750 !important;
                    color: var(--index-text-heading) !important;
                    font-size: 0.88rem !important;
                    cursor: pointer;
                    margin: 0 !important;
                    padding: 0 0.25rem 0 0 !important;
                    box-shadow: none !important;
                }

                .staff-scope .filter-select option {
                    background-color: var(--index-card-bg, #282a3a) !important;
                    color: var(--index-text-heading, #ffffff) !important;
                    padding: 8px 12px;
                }

                :root:not(.dark) .staff-scope .filter-select option {
                    background-color: #ffffff !important;
                    color: #0f172a !important;
                }

                @media (max-width: 768px) {
                    .staff-scope .search-filter-belt {
                        flex-direction: column;
                        gap: 0.75rem;
                        width: 100%;
                    }
                    .staff-scope .search-box-wrapper,
                    .staff-scope .filter-dropdown-wrapper {
                        width: 100% !important;
                        min-width: 100% !important;
                    }
                }

                /* Category Chips */
                .staff-scope .category-chips-row {
                    display: flex;
                    gap: 0.5rem;
                    overflow-x: auto;
                    padding-bottom: 0.5rem;
                    margin-bottom: 1.75rem;
                    scrollbar-width: none;
                }
                .staff-scope .category-chips-row::-webkit-scrollbar {
                    display: none;
                }

                .staff-scope .category-chip-btn {
                    padding: 0.5rem 1rem;
                    border-radius: 10px;
                    font-weight: 800;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: all 0.2s;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.4rem;
                }

                .staff-scope .category-chip-btn.active {
                    background: var(--index-text-heading);
                    color: var(--index-card-bg);
                    border: 1.5px solid var(--index-text-heading);
                }

                .staff-scope .category-chip-btn:not(.active) {
                    background: var(--index-card-bg, #ffffff);
                    color: var(--index-text-secondary);
                    border: 1.5px solid var(--index-border-color, #e2e8f0);
                }

                .staff-scope .category-chip-btn:not(.active):hover {
                    border-color: var(--index-border-subtle);
                    color: var(--index-text-heading);
                }

                /* Document Cards Grid */
                .staff-scope .doc-cards-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
                    gap: 1.5rem;
                }

                .staff-scope .doc-card-item {
                    background: var(--index-card-bg, #ffffff);
                    border: 1.5px solid var(--index-border-color, #e2e8f0);
                    border-radius: 20px;
                    padding: 1.5rem;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    gap: 1.25rem;
                    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
                }

                .staff-scope .doc-card-item:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 30px -8px rgba(0, 0, 0, 0.08);
                    border-color: var(--index-primary-color);
                }

                /* Standard Table Card */
                .staff-scope .job-support-table-card {
                    background: var(--index-card-bg, #ffffff);
                    border: 1.5px solid var(--index-border-color, #e2e8f0);
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                }

                .staff-scope .job-support-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .staff-scope .job-support-table th {
                    text-align: left;
                    padding: 1rem 1.25rem;
                    background: var(--index-hover-bg, rgba(0,0,0,0.02));
                    color: var(--index-text-secondary);
                    font-size: 0.72rem;
                    font-weight: 850;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    border-bottom: 1.5px solid var(--index-border-color);
                }

                .staff-scope .job-support-table td {
                    padding: 1.1rem 1.25rem;
                    border-bottom: 1px solid var(--index-border-color);
                    color: var(--index-text-heading);
                    font-size: 0.88rem;
                }

                .staff-scope .job-support-table tr:hover td {
                    background: var(--index-hover-bg);
                }
            `}</style>

            {/* Standard Header */}
            <div className="users-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'var(--index-accent-soft-bg)',
                            color: 'var(--index-primary-color)',
                            padding: '4px 10px',
                            borderRadius: '8px',
                            fontSize: '0.72rem',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            <Briefcase size={12} /> {isAdmin ? 'Super Admin Portal' : 'Instructor Portal'}
                        </span>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'rgba(16, 185, 129, 0.12)',
                            color: '#10b981',
                            padding: '4px 10px',
                            borderRadius: '8px',
                            fontSize: '0.72rem',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            <Shield size={12} /> Anti-Download Protected
                        </span>
                    </div>
                    <h2>Job Support & Career Repository</h2>
                    <p>
                        Upload proprietary project documentation (.pdf, .docx, .pptx) and assign protected in-app viewing access to students without download permissions.
                    </p>
                </div>

                <div className="header-actions">
                    <button
                        type="button"
                        onClick={() => loadData(true)}
                        disabled={refreshing}
                        className="btn-export"
                        title="Refresh Repository Data"
                    >
                        <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} /> Refresh
                    </button>

                    <button
                        type="button"
                        onClick={handleOpenUpload}
                        className="btn-add-user"
                    >
                        <Plus size={18} strokeWidth={2.5} /> Upload Document
                    </button>
                </div>
            </div>

            {/* 4 Stats Cards Grid */}
            <div className="stats-grid-dashboard">
                {/* 1. Total Documents */}
                <div className="stat-card-premium">
                    <div className="stat-icon-wrapper" style={{ background: 'var(--index-accent-soft-bg)', color: 'var(--index-primary-color)' }}>
                        <FileText size={24} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--index-text-heading)', lineHeight: 1.1 }}>
                            {materials.length}
                        </div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--index-text-faint)', letterSpacing: '0.04em', marginTop: '3px' }}>
                            Total Documents
                        </div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--index-text-secondary)', marginTop: '2px' }}>
                            {formatBytes(totalStorageBytes)} in storage
                        </div>
                    </div>
                </div>

                {/* 2. Granted Students */}
                <div className="stat-card-premium">
                    <div className="stat-icon-wrapper" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>
                        <Users size={24} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--index-text-heading)', lineHeight: 1.1 }}>
                            {totalStudentsWithAccess} <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--index-text-faint)' }}>/ {students.length}</span>
                        </div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--index-text-faint)', letterSpacing: '0.04em', marginTop: '3px' }}>
                            Granted Students
                        </div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#10b981', marginTop: '2px' }}>
                            {students.length > 0 ? Math.round((totalStudentsWithAccess / students.length) * 100) : 0}% of student roster
                        </div>
                    </div>
                </div>

                {/* 3. Format Variety */}
                <div className="stat-card-premium">
                    <div className="stat-icon-wrapper" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' }}>
                        <Presentation size={24} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center', minHeight: '26px' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: 800, padding: '2px 7px', borderRadius: '6px', background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' }}>
                                {formatDistribution.pdf} PDF
                            </span>
                            <span style={{ fontSize: '0.72rem', fontWeight: 800, padding: '2px 7px', borderRadius: '6px', background: 'rgba(2, 132, 199, 0.12)', color: '#0284c7' }}>
                                {formatDistribution.docx} DOCX
                            </span>
                            <span style={{ fontSize: '0.72rem', fontWeight: 800, padding: '2px 7px', borderRadius: '6px', background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' }}>
                                {formatDistribution.pptx} PPTX
                            </span>
                        </div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--index-text-faint)', letterSpacing: '0.04em', marginTop: '4px' }}>
                            Format Variety
                        </div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--index-text-secondary)', marginTop: '2px' }}>
                            Multi-format rendering
                        </div>
                    </div>
                </div>

                {/* 4. Security Status */}
                <div className="stat-card-premium">
                    <div className="stat-icon-wrapper" style={{ background: 'rgba(99, 102, 241, 0.12)', color: '#6366f1' }}>
                        <Shield size={24} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '1.15rem', fontWeight: 900, color: '#6366f1', display: 'flex', alignItems: 'center', gap: '4px', lineHeight: 1.2 }}>
                            <Check size={18} strokeWidth={3} /> Zero-Download
                        </div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--index-text-faint)', letterSpacing: '0.04em', marginTop: '3px' }}>
                            Security Engine
                        </div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--index-text-secondary)', marginTop: '2px' }}>
                            Watermarking & Print Block
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs Bar */}
            <div className="job-support-tab-bar">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        onClick={() => setActiveTab('materials')}
                        className={`job-support-tab-btn ${activeTab === 'materials' ? 'active' : ''}`}
                    >
                        <Briefcase size={16} /> Documents & Materials ({materials.length})
                    </button>

                    <button
                        type="button"
                        onClick={() => setActiveTab('access')}
                        className={`job-support-tab-btn ${activeTab === 'access' ? 'active' : ''}`}
                    >
                        <UserCheck size={16} /> Student Access Gating ({totalStudentsWithAccess} / {students.length})
                    </button>
                </div>

                {activeTab === 'materials' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'var(--index-hover-bg)', padding: '4px', borderRadius: '12px', border: '1px solid var(--index-border-color)' }}>
                        <button
                            type="button"
                            onClick={() => setViewMode('grid')}
                            style={{
                                padding: '6px 10px',
                                borderRadius: '8px',
                                border: 'none',
                                background: viewMode === 'grid' ? 'var(--index-card-bg)' : 'transparent',
                                color: viewMode === 'grid' ? 'var(--index-primary-color)' : 'var(--index-text-faint)',
                                boxShadow: viewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            title="Grid View"
                        >
                            <LayoutGrid size={16} />
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewMode('table')}
                            style={{
                                padding: '6px 10px',
                                borderRadius: '8px',
                                border: 'none',
                                background: viewMode === 'table' ? 'var(--index-card-bg)' : 'transparent',
                                color: viewMode === 'table' ? 'var(--index-primary-color)' : 'var(--index-text-faint)',
                                boxShadow: viewMode === 'table' ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            title="Table View"
                        >
                            <List size={16} />
                        </button>
                    </div>
                )}
            </div>

            {/* ========================================================================= */}
            {/* TAB 1: DOCUMENTS & MATERIALS REPOSITORY */}
            {/* ========================================================================= */}
            {activeTab === 'materials' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {/* Standard Search & Filter Belt */}
                    <div className="search-filter-belt">
                        <div className="search-box-wrapper">
                            <Search size={18} style={{ color: 'var(--index-text-faint)', flexShrink: 0 }} />
                            <input
                                type="text"
                                placeholder="Search documents by title, briefing, category..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--index-text-faint)', display: 'flex' }}
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        <div className="filter-dropdown-wrapper">
                            <Filter size={16} style={{ color: 'var(--index-text-faint)', flexShrink: 0 }} />
                            <span className="filter-label">Format:</span>
                            <select
                                value={selectedFormat}
                                onChange={e => setSelectedFormat(e.target.value)}
                                className="filter-select"
                            >
                                <option value="All">All Formats</option>
                                <option value="pdf">PDF Documents</option>
                                <option value="docx">Word (.docx)</option>
                                <option value="pptx">PowerPoint (.pptx)</option>
                            </select>
                        </div>

                        <div className="filter-dropdown-wrapper">
                            <ArrowUpDown size={16} style={{ color: 'var(--index-text-faint)', flexShrink: 0 }} />
                            <span className="filter-label">Sort:</span>
                            <select
                                value={sortBy}
                                onChange={e => setSortBy(e.target.value as any)}
                                className="filter-select"
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="title">Title (A - Z)</option>
                                <option value="size">File Size</option>
                            </select>
                        </div>
                    </div>

                    {/* Category Filter Chips */}
                    <div className="category-chips-row">
                        {uniqueCategories.map(cat => {
                            const count = cat === 'All' ? materials.length : materials.filter(m => m.category === cat).length;
                            const isSelected = selectedCategory === cat;

                            return (
                                <button
                                    key={cat}
                                    type="button"
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`category-chip-btn ${isSelected ? 'active' : ''}`}
                                >
                                    <span>{cat}</span>
                                    <span style={{
                                        fontSize: '0.68rem',
                                        padding: '1px 6px',
                                        borderRadius: '99px',
                                        background: isSelected ? 'rgba(255,255,255,0.25)' : 'var(--index-hover-bg)',
                                        color: isSelected ? '#ffffff' : 'var(--index-text-faint)'
                                    }}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Documents List / Grid */}
                    {filteredMaterials.length === 0 ? (
                        <div style={{
                            background: 'var(--index-card-bg)',
                            border: '2px dashed var(--index-border-color)',
                            borderRadius: '24px',
                            padding: '3.5rem 1.5rem',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '1rem'
                        }}>
                            <div style={{ width: '64px', height: '64px', borderRadius: '20px', background: 'var(--index-hover-bg)', color: 'var(--index-text-faint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Briefcase size={32} />
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--index-text-heading)', margin: 0 }}>
                                    No documents found
                                </h3>
                                <p style={{ fontSize: '0.85rem', color: 'var(--index-text-secondary)', margin: '0.35rem 0 0 0', maxWidth: '380px' }}>
                                    {searchQuery ? 'Try clearing your search term or choosing a different category filter.' : 'Click "Upload Document" to upload your first Job Support file.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleOpenUpload}
                                className="btn-add-user"
                                style={{ marginTop: '0.5rem' }}
                            >
                                <Plus size={16} /> Upload Now
                            </button>
                        </div>
                    ) : viewMode === 'grid' ? (
                        /* GRID VIEW */
                        <div className="doc-cards-grid">
                            {filteredMaterials.map(item => {
                                const badge = getFormatBadge(item.file_type);
                                const FormatIcon = badge.icon;

                                return (
                                    <div key={item.id} className="doc-card-item">
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                            {/* Top badges */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <div style={{
                                                    width: '48px',
                                                    height: '48px',
                                                    borderRadius: '14px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: `${badge.accentColor}15`,
                                                    borderColor: `${badge.accentColor}30`,
                                                    color: badge.accentColor,
                                                    border: '1px solid',
                                                    flexShrink: 0
                                                }}>
                                                    <FormatIcon size={22} />
                                                </div>

                                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                    <span style={{
                                                        padding: '3px 9px',
                                                        borderRadius: '99px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 850,
                                                        textTransform: 'uppercase',
                                                        backgroundColor: `${badge.accentColor}15`,
                                                        color: badge.accentColor,
                                                        border: `1px solid ${badge.accentColor}30`
                                                    }}>
                                                        {badge.label}
                                                    </span>
                                                    <span style={{
                                                        padding: '3px 9px',
                                                        borderRadius: '99px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 700,
                                                        background: 'var(--index-hover-bg)',
                                                        color: 'var(--index-text-secondary)',
                                                        border: '1px solid var(--index-border-color)'
                                                    }}>
                                                        {item.category || 'General'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Title & Description */}
                                            <div>
                                                <h3 style={{
                                                    fontSize: '1rem',
                                                    fontWeight: 850,
                                                    color: 'var(--index-text-heading)',
                                                    margin: '0 0 0.4rem 0',
                                                    lineHeight: 1.35
                                                }}>
                                                    {item.title}
                                                </h3>
                                                {item.description ? (
                                                    <p style={{
                                                        fontSize: '0.8rem',
                                                        color: 'var(--index-text-secondary)',
                                                        lineHeight: 1.5,
                                                        margin: 0,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}>
                                                        {item.description}
                                                    </p>
                                                ) : (
                                                    <p style={{ fontSize: '0.8rem', color: 'var(--index-text-faint)', fontStyle: 'italic', margin: 0 }}>
                                                        No description provided.
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Card Footer & Actions */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', paddingTop: '0.85rem', borderTop: '1px solid var(--index-border-color)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', fontWeight: 600, color: 'var(--index-text-secondary)' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <HardDrive size={13} style={{ color: 'var(--index-text-faint)' }} />
                                                    {formatBytes(item.file_size)}
                                                </span>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <Calendar size={13} style={{ color: 'var(--index-text-faint)' }} />
                                                    {formatDate(item.created_at)}
                                                </span>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewDoc(item)}
                                                    style={{
                                                        gridColumn: 'span 2',
                                                        padding: '0.6rem 0.75rem',
                                                        borderRadius: '10px',
                                                        border: 'none',
                                                        background: 'var(--index-primary-color)',
                                                        color: '#ffffff',
                                                        fontWeight: 800,
                                                        fontSize: '0.75rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <Eye size={15} /> Preview
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenAssignModal(item)}
                                                    style={{
                                                        padding: '0.6rem 0.5rem',
                                                        borderRadius: '10px',
                                                        border: '1.5px solid var(--index-border-color)',
                                                        background: 'var(--index-hover-bg)',
                                                        color: 'var(--index-text-heading)',
                                                        fontWeight: 750,
                                                        fontSize: '0.75rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer'
                                                    }}
                                                    title="Assign specific student permissions"
                                                >
                                                    <Users size={15} />
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenEdit(item)}
                                                    style={{
                                                        padding: '0.6rem 0.5rem',
                                                        borderRadius: '10px',
                                                        border: '1.5px solid var(--index-border-color)',
                                                        background: 'var(--index-hover-bg)',
                                                        color: 'var(--index-text-heading)',
                                                        fontWeight: 750,
                                                        fontSize: '0.75rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer'
                                                    }}
                                                    title="Edit document details"
                                                >
                                                    <Edit3 size={15} />
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(item)}
                                                    style={{
                                                        gridColumn: 'span 4',
                                                        padding: '0.5rem',
                                                        borderRadius: '10px',
                                                        border: '1px solid rgba(239, 68, 68, 0.25)',
                                                        background: 'rgba(239, 68, 68, 0.08)',
                                                        color: '#ef4444',
                                                        fontWeight: 750,
                                                        fontSize: '0.72rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '4px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <Trash2 size={13} /> Delete Document
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        /* TABLE VIEW */
                        <div className="job-support-table-card">
                            <div style={{ overflowX: 'auto' }}>
                                <table className="job-support-table">
                                    <thead>
                                        <tr>
                                            <th>Document Title</th>
                                            <th>Category</th>
                                            <th>Format</th>
                                            <th>File Size</th>
                                            <th>Date Uploaded</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredMaterials.map(item => {
                                            const badge = getFormatBadge(item.file_type);
                                            const FormatIcon = badge.icon;

                                            return (
                                                <tr key={item.id}>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                            <div style={{
                                                                width: '38px',
                                                                height: '38px',
                                                                borderRadius: '10px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                backgroundColor: `${badge.accentColor}15`,
                                                                color: badge.accentColor,
                                                                border: `1px solid ${badge.accentColor}30`,
                                                                flexShrink: 0
                                                            }}>
                                                                <FormatIcon size={18} />
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: 850, color: 'var(--index-text-heading)' }}>
                                                                    {item.title}
                                                                </div>
                                                                <div style={{ fontSize: '0.72rem', color: 'var(--index-text-faint)' }}>
                                                                    {item.original_file_name}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span style={{ padding: '3px 8px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: 700, background: 'var(--index-hover-bg)', color: 'var(--index-text-secondary)', border: '1px solid var(--index-border-color)' }}>
                                                            {item.category || 'General'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span style={{ padding: '2px 8px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: 850, textTransform: 'uppercase', backgroundColor: `${badge.accentColor}15`, color: badge.accentColor, border: `1px solid ${badge.accentColor}30` }}>
                                                            {badge.label}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--index-text-secondary)' }}>
                                                        {formatBytes(item.file_size)}
                                                    </td>
                                                    <td style={{ color: 'var(--index-text-secondary)', fontWeight: 600 }}>
                                                        {formatDate(item.created_at)}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.35rem' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => setPreviewDoc(item)}
                                                                style={{ padding: '6px 10px', borderRadius: '8px', border: 'none', background: 'var(--index-primary-color)', color: '#ffffff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                                                                title="Preview"
                                                            >
                                                                <Eye size={14} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleOpenAssignModal(item)}
                                                                style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--index-border-color)', background: 'var(--index-hover-bg)', color: 'var(--index-text-heading)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                                                                title="Assign Students"
                                                            >
                                                                <Users size={14} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleOpenEdit(item)}
                                                                style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--index-border-color)', background: 'var(--index-hover-bg)', color: 'var(--index-text-heading)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                                                                title="Edit"
                                                            >
                                                                <Edit3 size={14} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDelete(item)}
                                                                style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.3)', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ========================================================================= */}
            {/* TAB 2: STUDENT ACCESS GATING & BATCH PERMISSIONS */}
            {/* ========================================================================= */}
            {activeTab === 'access' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {/* Standard Search & Filter Belt for Students */}
                    <div className="search-filter-belt">
                        <div className="search-box-wrapper">
                            <Search size={18} style={{ color: 'var(--index-text-faint)', flexShrink: 0 }} />
                            <input
                                type="text"
                                placeholder="Search student by name, email, phone..."
                                value={studentSearch}
                                onChange={e => setStudentSearch(e.target.value)}
                                className="search-input"
                            />
                            {studentSearch && (
                                <button
                                    type="button"
                                    onClick={() => setStudentSearch('')}
                                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--index-text-faint)', display: 'flex' }}
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        <div className="filter-dropdown-wrapper">
                            <Users size={16} style={{ color: 'var(--index-text-faint)', flexShrink: 0 }} />
                            <span className="filter-label">Cohort:</span>
                            <select
                                value={selectedCohort}
                                onChange={e => setSelectedCohort(e.target.value)}
                                className="filter-select"
                            >
                                <option value="All">All Cohorts ({cohorts.length})</option>
                                {cohorts.map(c => (
                                    <option key={c.id} value={c.name}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-dropdown-wrapper">
                            <Filter size={16} style={{ color: 'var(--index-text-faint)', flexShrink: 0 }} />
                            <span className="filter-label">Status:</span>
                            <select
                                value={selectedAccessStatus}
                                onChange={e => setSelectedAccessStatus(e.target.value as any)}
                                className="filter-select"
                            >
                                <option value="all">All Students</option>
                                <option value="granted">Access Granted ({totalStudentsWithAccess})</option>
                                <option value="locked">Access Locked ({students.length - totalStudentsWithAccess})</option>
                            </select>
                        </div>
                    </div>

                    {/* Batch Action Belt */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.85rem 1.25rem',
                        borderRadius: '14px',
                        background: 'var(--index-card-bg)',
                        border: '1.5px solid var(--index-border-color)',
                        gap: '1rem',
                        flexWrap: 'wrap'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <button
                                type="button"
                                onClick={handleSelectAllFilteredStudents}
                                style={{
                                    padding: '0.55rem 1rem',
                                    borderRadius: '10px',
                                    border: '1.5px solid var(--index-border-color)',
                                    background: 'var(--index-hover-bg)',
                                    color: 'var(--index-text-heading)',
                                    fontWeight: 750,
                                    fontSize: '0.78rem',
                                    cursor: 'pointer'
                                }}
                            >
                                {selectedStudentIds.length === filteredStudents.length && filteredStudents.length > 0
                                    ? 'Deselect All'
                                    : `Select All (${filteredStudents.length})`}
                            </button>
                            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--index-text-secondary)' }}>
                                {selectedStudentIds.length} student(s) selected
                            </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                disabled={selectedStudentIds.length === 0}
                                onClick={() => handleBulkAccessToggle(true)}
                                style={{
                                    padding: '0.6rem 1.1rem',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: '#10b981',
                                    color: '#ffffff',
                                    fontWeight: 800,
                                    fontSize: '0.78rem',
                                    textTransform: 'uppercase',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    cursor: selectedStudentIds.length === 0 ? 'not-allowed' : 'pointer',
                                    opacity: selectedStudentIds.length === 0 ? 0.4 : 1
                                }}
                            >
                                <UserCheck size={14} /> Grant Access ({selectedStudentIds.length})
                            </button>

                            <button
                                type="button"
                                disabled={selectedStudentIds.length === 0}
                                onClick={() => handleBulkAccessToggle(false)}
                                style={{
                                    padding: '0.6rem 1.1rem',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: '#ef4444',
                                    color: '#ffffff',
                                    fontWeight: 800,
                                    fontSize: '0.78rem',
                                    textTransform: 'uppercase',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    cursor: selectedStudentIds.length === 0 ? 'not-allowed' : 'pointer',
                                    opacity: selectedStudentIds.length === 0 ? 0.4 : 1
                                }}
                            >
                                <UserX size={14} /> Revoke Access ({selectedStudentIds.length})
                            </button>
                        </div>
                    </div>

                    {/* Students Access Table */}
                    <div className="job-support-table-card">
                        <div style={{ overflowX: 'auto' }}>
                            <table className="job-support-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px', textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedStudentIds.length === filteredStudents.length && filteredStudents.length > 0}
                                                onChange={handleSelectAllFilteredStudents}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </th>
                                        <th>Student Information</th>
                                        <th>Assigned Cohort</th>
                                        <th style={{ textAlign: 'center' }}>Portal Access Status</th>
                                        <th style={{ textAlign: 'center' }}>Document Permissions</th>
                                        <th style={{ textAlign: 'right' }}>Quick Toggle</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedStudents.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} style={{ padding: '3rem 1rem', textAlign: 'center', color: 'var(--index-text-faint)', fontWeight: 700 }}>
                                                No students found matching current filters.
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedStudents.map(student => {
                                            const isSelected = selectedStudentIds.includes(student.id);
                                            const studentInitial = (student.name || 'S').charAt(0).toUpperCase();

                                            return (
                                                <tr key={student.id} style={{ background: student.has_job_support ? 'rgba(16, 185, 129, 0.02)' : 'transparent' }}>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => {
                                                                setSelectedStudentIds(prev =>
                                                                    prev.includes(student.id)
                                                                        ? prev.filter(id => id !== student.id)
                                                                        : [...prev, student.id]
                                                                );
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                            <div style={{
                                                                width: '38px',
                                                                height: '38px',
                                                                borderRadius: '12px',
                                                                background: 'var(--index-hover-bg)',
                                                                color: 'var(--index-text-heading)',
                                                                fontWeight: 900,
                                                                fontSize: '0.9rem',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0
                                                            }}>
                                                                {studentInitial}
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: 850, color: 'var(--index-text-heading)' }}>
                                                                    {student.name}
                                                                </div>
                                                                <div style={{ fontSize: '0.75rem', color: 'var(--index-text-faint)' }}>
                                                                    {student.email}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {student.cohorts.length > 0 ? (
                                                            <span style={{ padding: '3px 9px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: 750, background: 'var(--index-hover-bg)', color: 'var(--index-text-secondary)', border: '1px solid var(--index-border-color)' }}>
                                                                {student.cohorts.join(', ')}
                                                            </span>
                                                        ) : (
                                                            <span style={{ fontSize: '0.75rem', color: 'var(--index-text-faint)', fontStyle: 'italic' }}>
                                                                No Cohort
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {student.has_job_support ? (
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', background: 'rgba(16, 185, 129, 0.12)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                                                                <Unlock size={12} strokeWidth={2.5} /> Assigned (Unlocked)
                                                            </span>
                                                        ) : (
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', background: 'var(--index-hover-bg)', color: 'var(--index-text-faint)', border: '1px solid var(--index-border-color)' }}>
                                                                <Lock size={12} strokeWidth={2.5} /> Locked (No Access)
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {student.has_general_access ? (
                                                            <span style={{ padding: '3px 9px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: 750, background: 'rgba(2, 132, 199, 0.12)', color: '#0284c7', border: '1px solid rgba(2, 132, 199, 0.25)' }}>
                                                                Full Library Access
                                                            </span>
                                                        ) : student.assigned_materials_count > 0 ? (
                                                            <span style={{ padding: '3px 9px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: 750, background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.25)' }}>
                                                                {student.assigned_materials_count} specific doc(s)
                                                            </span>
                                                        ) : (
                                                            <span style={{ fontSize: '0.75rem', color: 'var(--index-text-faint)' }}>None</span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggleStudentAccess(student.id, !student.has_job_support)}
                                                            style={{
                                                                padding: '0.5rem 1rem',
                                                                borderRadius: '10px',
                                                                border: student.has_job_support ? '1px solid rgba(239, 68, 68, 0.3)' : 'none',
                                                                background: student.has_job_support ? 'rgba(239, 68, 68, 0.08)' : '#10b981',
                                                                color: student.has_job_support ? '#ef4444' : '#ffffff',
                                                                fontWeight: 800,
                                                                fontSize: '0.75rem',
                                                                textTransform: 'uppercase',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            {student.has_job_support ? 'Revoke Access' : 'Grant Access'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Footer */}
                        <div style={{
                            padding: '1rem 1.25rem',
                            borderTop: '1.5px solid var(--index-border-color)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '1rem',
                            flexWrap: 'wrap',
                            fontSize: '0.8rem',
                            color: 'var(--index-text-secondary)'
                        }}>
                            <div>
                                Showing <strong>{filteredStudents.length > 0 ? (studentCurrentPage - 1) * studentsPerPage + 1 : 0}</strong> to <strong>{Math.min(studentCurrentPage * studentsPerPage, filteredStudents.length)}</strong> of <strong>{filteredStudents.length}</strong> students
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <select
                                    value={studentsPerPage}
                                    onChange={e => setStudentsPerPage(Number(e.target.value))}
                                    className="filter-select"
                                    style={{ padding: '4px 8px', border: '1px solid var(--index-border-color)', borderRadius: '8px' }}
                                >
                                    <option value={10}>10 per page</option>
                                    <option value={15}>15 per page</option>
                                    <option value={25}>25 per page</option>
                                    <option value={50}>50 per page</option>
                                </select>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <button
                                        type="button"
                                        disabled={studentCurrentPage <= 1}
                                        onClick={() => setStudentCurrentPage(p => Math.max(1, p - 1))}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '8px',
                                            border: '1.5px solid var(--index-border-color)',
                                            background: 'var(--index-card-bg)',
                                            color: 'var(--index-text-heading)',
                                            cursor: studentCurrentPage <= 1 ? 'not-allowed' : 'pointer',
                                            opacity: studentCurrentPage <= 1 ? 0.3 : 1
                                        }}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>

                                    <span style={{ padding: '0 8px', fontWeight: 800, color: 'var(--index-text-heading)' }}>
                                        {studentCurrentPage} / {totalStudentPages}
                                    </span>

                                    <button
                                        type="button"
                                        disabled={studentCurrentPage >= totalStudentPages}
                                        onClick={() => setStudentCurrentPage(p => Math.min(totalStudentPages, p + 1))}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '8px',
                                            border: '1.5px solid var(--index-border-color)',
                                            background: 'var(--index-card-bg)',
                                            color: 'var(--index-text-heading)',
                                            cursor: studentCurrentPage >= totalStudentPages ? 'not-allowed' : 'pointer',
                                            opacity: studentCurrentPage >= totalStudentPages ? 0.3 : 1
                                        }}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ========================================================================= */}
            {/* MODAL: UPLOAD / EDIT DOCUMENT */}
            {/* ========================================================================= */}
            {uploadModalOpen && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    background: 'rgba(15, 23, 42, 0.7)',
                    backdropFilter: 'blur(6px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                    overflowY: 'auto'
                }}>
                    <div style={{
                        background: 'var(--index-card-bg, #ffffff)',
                        border: '1.5px solid var(--index-border-color, #e2e8f0)',
                        borderRadius: '24px',
                        maxWidth: '560px',
                        width: '100%',
                        padding: '1.75rem',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.25rem'
                    }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1.5px solid var(--index-border-color)', paddingBottom: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'var(--index-primary-color)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Upload size={22} />
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--index-text-heading)', margin: 0 }}>
                                        {editModalDoc ? 'Edit Document Details' : 'Upload Job Support Document'}
                                    </h3>
                                    <p style={{ fontSize: '0.78rem', color: 'var(--index-text-secondary)', margin: '2px 0 0 0' }}>
                                        Formats: .pdf, .docx, .pptx (Max 100MB)
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setUploadModalOpen(false)}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--index-text-faint)', padding: '4px' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmitUpload} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {/* Title */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--index-text-heading)', marginBottom: '0.35rem' }}>
                                    Document Title <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <div className="search-box-wrapper" style={{ height: '46px', width: '100%' }}>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. Primavera P6 Baseline Schedule Template"
                                        value={formTitle}
                                        onChange={e => setFormTitle(e.target.value)}
                                        className="search-input"
                                    />
                                </div>
                            </div>

                            {/* Category Selector */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--index-text-heading)', marginBottom: '0.35rem' }}>
                                    Category
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.4rem' }}>
                                    {categoryPresets.map(preset => (
                                        <button
                                            key={preset}
                                            type="button"
                                            onClick={() => setFormCategory(preset)}
                                            style={{
                                                padding: '0.5rem 0.6rem',
                                                borderRadius: '8px',
                                                border: formCategory === preset ? '1.5px solid var(--index-primary-color)' : '1px solid var(--index-border-color)',
                                                background: formCategory === preset ? 'var(--index-primary-color)' : 'var(--index-hover-bg)',
                                                color: formCategory === preset ? '#ffffff' : 'var(--index-text-heading)',
                                                fontSize: '0.72rem',
                                                fontWeight: 800,
                                                cursor: 'pointer',
                                                textAlign: 'center'
                                            }}
                                        >
                                            {preset}
                                        </button>
                                    ))}
                                </div>

                                {formCategory === '+ Custom Category' && (
                                    <div className="search-box-wrapper" style={{ height: '44px', width: '100%', marginTop: '0.5rem' }}>
                                        <input
                                            type="text"
                                            placeholder="Type custom category name..."
                                            value={customCategory}
                                            onChange={e => setCustomCategory(e.target.value)}
                                            className="search-input"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Description */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--index-text-heading)', marginBottom: '0.35rem' }}>
                                    Briefing / Description
                                </label>
                                <textarea
                                    rows={3}
                                    placeholder="Summary of what this document covers..."
                                    value={formDescription}
                                    onChange={e => setFormDescription(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        borderRadius: '12px',
                                        border: '1.5px solid var(--index-border-color)',
                                        background: 'var(--index-card-bg)',
                                        color: 'var(--index-text-heading)',
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        outline: 'none',
                                        resize: 'none'
                                    }}
                                />
                            </div>

                            {/* File Upload Dropzone */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--index-text-heading)', marginBottom: '0.35rem' }}>
                                    {editModalDoc ? 'Replace File (Optional)' : 'Select Document File *'}
                                </label>
                                <div style={{
                                    border: '2px dashed var(--index-border-color)',
                                    borderRadius: '16px',
                                    padding: '1.5rem 1rem',
                                    textAlign: 'center',
                                    position: 'relative',
                                    background: 'var(--index-hover-bg)',
                                    cursor: 'pointer'
                                }}>
                                    <input
                                        type="file"
                                        accept=".pdf,.doc,.docx,.ppt,.pptx"
                                        onChange={e => {
                                            if (e.target.files && e.target.files[0]) {
                                                setFormFile(e.target.files[0]);
                                            }
                                        }}
                                        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                    />
                                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(6, 182, 212, 0.12)', color: '#06b6d4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.5rem auto' }}>
                                        <Upload size={22} />
                                    </div>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--index-text-heading)' }}>
                                        {formFile ? formFile.name : (editModalDoc ? `Current: ${editModalDoc.original_file_name}` : 'Click or drop file here')}
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: 'var(--index-text-faint)', marginTop: '2px' }}>
                                        {formFile ? formatBytes(formFile.size) : 'PDF, Word, or PowerPoint presentations up to 100MB'}
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '0.75rem', borderTop: '1.5px solid var(--index-border-color)' }}>
                                <button
                                    type="button"
                                    onClick={() => setUploadModalOpen(false)}
                                    style={{
                                        padding: '0.65rem 1.25rem',
                                        borderRadius: '10px',
                                        border: '1.5px solid var(--index-border-color)',
                                        background: 'var(--index-hover-bg)',
                                        color: 'var(--index-text-heading)',
                                        fontWeight: 750,
                                        fontSize: '0.8rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="btn-add-user"
                                    style={{ height: 'auto', padding: '0.65rem 1.4rem' }}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 size={15} className="animate-spin" /> Uploading...
                                        </>
                                    ) : (
                                        editModalDoc ? 'Save Changes' : 'Upload Document'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ========================================================================= */}
            {/* MODAL: ASSIGN SPECIFIC DOCUMENT TO STUDENTS */}
            {/* ========================================================================= */}
            {assignModalDoc && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    background: 'rgba(15, 23, 42, 0.7)',
                    backdropFilter: 'blur(6px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                    overflowY: 'auto'
                }}>
                    <div style={{
                        background: 'var(--index-card-bg, #ffffff)',
                        border: '1.5px solid var(--index-border-color, #e2e8f0)',
                        borderRadius: '24px',
                        maxWidth: '600px',
                        width: '100%',
                        padding: '1.75rem',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.25rem'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1.5px solid var(--index-border-color)', paddingBottom: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(2, 132, 199, 0.12)', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Users size={22} />
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--index-text-heading)', margin: 0 }}>
                                        Assign Document Access
                                    </h3>
                                    <p style={{ fontSize: '0.78rem', color: 'var(--index-text-secondary)', margin: '2px 0 0 0', maxWidth: '380px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        "{assignModalDoc.title}"
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setAssignModalDoc(null)}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--index-text-faint)', padding: '4px' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Search & Actions */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <div className="search-box-wrapper" style={{ height: '44px', flex: 1, minWidth: '180px' }}>
                                    <Search size={16} style={{ color: 'var(--index-text-faint)' }} />
                                    <input
                                        type="text"
                                        placeholder="Filter student list..."
                                        value={assignSearchQuery}
                                        onChange={e => setAssignSearchQuery(e.target.value)}
                                        className="search-input"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setAssignTargetStudentIds(students.map(s => s.id))}
                                    style={{ padding: '0 0.85rem', borderRadius: '10px', border: '1.5px solid var(--index-border-color)', background: 'var(--index-hover-bg)', color: 'var(--index-text-heading)', fontWeight: 750, fontSize: '0.75rem', cursor: 'pointer', height: '44px' }}
                                >
                                    Select All
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setAssignTargetStudentIds([])}
                                    style={{ padding: '0 0.85rem', borderRadius: '10px', border: '1.5px solid var(--index-border-color)', background: 'var(--index-hover-bg)', color: 'var(--index-text-heading)', fontWeight: 750, fontSize: '0.75rem', cursor: 'pointer', height: '44px' }}
                                >
                                    Clear
                                </button>
                            </div>

                            {/* Checkbox List */}
                            <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1.5px solid var(--index-border-color)', borderRadius: '14px', padding: '4px' }}>
                                {students
                                    .filter(s =>
                                        s.name.toLowerCase().includes(assignSearchQuery.toLowerCase()) ||
                                        s.email.toLowerCase().includes(assignSearchQuery.toLowerCase())
                                    )
                                    .map(student => {
                                        const isAssigned = assignTargetStudentIds.includes(student.id);

                                        return (
                                            <div
                                                key={student.id}
                                                onClick={() => {
                                                    setAssignTargetStudentIds(prev =>
                                                        prev.includes(student.id)
                                                            ? prev.filter(id => id !== student.id)
                                                            : [...prev, student.id]
                                                    );
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '0.7rem 0.85rem',
                                                    borderRadius: '10px',
                                                    background: isAssigned ? 'rgba(2, 132, 199, 0.08)' : 'transparent',
                                                    cursor: 'pointer',
                                                    transition: 'background 0.15s'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isAssigned}
                                                        onChange={() => {}}
                                                        style={{ cursor: 'pointer', pointerEvents: 'none' }}
                                                    />
                                                    <div>
                                                        <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--index-text-heading)' }}>
                                                            {student.name}
                                                        </div>
                                                        <div style={{ fontSize: '0.72rem', color: 'var(--index-text-faint)' }}>
                                                            {student.email} &bull; {student.cohorts.join(', ') || 'No Cohort'}
                                                        </div>
                                                    </div>
                                                </div>

                                                <span style={{
                                                    fontSize: '0.68rem',
                                                    fontWeight: 850,
                                                    textTransform: 'uppercase',
                                                    padding: '2px 8px',
                                                    borderRadius: '99px',
                                                    background: isAssigned ? '#0284c7' : 'var(--index-hover-bg)',
                                                    color: isAssigned ? '#ffffff' : 'var(--index-text-faint)'
                                                }}>
                                                    {isAssigned ? 'Assigned' : 'Unassigned'}
                                                </span>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.75rem', borderTop: '1.5px solid var(--index-border-color)' }}>
                            <span style={{ fontSize: '0.8rem', fontWeight: 750, color: 'var(--index-text-secondary)' }}>
                                <strong style={{ color: '#0284c7' }}>{assignTargetStudentIds.length}</strong> students selected
                            </span>

                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                    type="button"
                                    onClick={() => setAssignModalDoc(null)}
                                    style={{
                                        padding: '0.65rem 1.25rem',
                                        borderRadius: '10px',
                                        border: '1.5px solid var(--index-border-color)',
                                        background: 'var(--index-hover-bg)',
                                        color: 'var(--index-text-heading)',
                                        fontWeight: 750,
                                        fontSize: '0.8rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={assignSaving}
                                    onClick={handleSaveMaterialAssignments}
                                    style={{
                                        padding: '0.65rem 1.25rem',
                                        borderRadius: '10px',
                                        border: 'none',
                                        background: '#0284c7',
                                        color: '#ffffff',
                                        fontWeight: 850,
                                        fontSize: '0.8rem',
                                        textTransform: 'uppercase',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {assignSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ========================================================================= */}
            {/* SECURE PROTECTED VIEWER OVERLAY */}
            {/* ========================================================================= */}
            {previewDoc && (
                <SecureDocumentViewer
                    documentId={previewDoc.id}
                    title={previewDoc.title}
                    fileType={previewDoc.file_type}
                    studentName={isAdmin ? 'ADMINISTRATOR PREVIEW' : 'INSTRUCTOR PREVIEW'}
                    studentEmail={user?.email}
                    onClose={() => setPreviewDoc(null)}
                />
            )}
        </div>
    );
}
